import { Component, Input } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { AbstractBuyerComponent } from "../abstract/buyer.component";
import { AuthService } from "../../service/auth.service";
import { AnalyticsService } from "../../service/analytics.service";
import { GeneralService } from "../../service/general.service";
import { Pair } from "../../obj/Pair";
import { User } from "../../entity/User";
import { Account } from "../../entity/Account";
import { AccountSelector } from "../../obj/AccountSelector";
import { FormsModule } from "@angular/forms";
import { DatePipe } from "@angular/common";
import { Company } from "../../entity/Company";

@Component({
    selector: "kilowant-account-selector",
    templateUrl: "../../../html/incs/account-selector.html",
    imports: [FormsModule, DatePipe]
})
export class AccountSelectorComponent extends AbstractBuyerComponent {

    @Input("account") set setValue(value: Account | null) {
        this.account = value;
    }
    @Input("isSelector") isSelector: boolean = true;

    user: User = new User();
    accounts: AccountSelector[] | null = null;
    filteredAccounts: AccountSelector[] | null = null;
    account: Account | null = null;

    client: User | null = null;
    clients: Pair[] = [];

    utilityName: string;
    supplierName: string;
    refreshAccount: boolean = false;
    withFilter: boolean = false;

    userFilter: Pair | null;

    keywords: string = "";

    noAccountsPerCurrentUser: boolean = false;

    constructor(
        router: Router,
        route: ActivatedRoute,
        authService: AuthService,
        analyticsService: AnalyticsService,
        generalService: GeneralService,
        dialog: MatDialog,
    ) {
        super(router, route, dialog, authService, analyticsService, generalService);
    }

    ngOnInit(): void {
        this.eventSubscriptions.push(this.generalService.changeClientActivities$.subscribe(
            client => {
                if (client != null) {
                    this.changeClient(client);
                }
            })
        );
        this.eventSubscriptions.push(this.generalService.newContractCreated$.subscribe(
            contract => {
                this.pageStart();
            }
        )
        );
        this.eventSubscriptions.push(this.generalService.changeAccountActivities$.subscribe(
            account => {
                this.account = this.generalService.getStoredAccount();
                if (this.account == null && !this.noAccountsPerCurrentUser) {
                    this.userFilter = this.generalService.getStoredUserFilter();
                    this.refreshAccounts();
                }
            }
        )
        );
        super.ngOnInit();
    }

    pageStart(): void {
        this.account = this.generalService.getStoredAccount();
        if (this.user.isAdvisor) {
            this.refreshClients();
        } else {
            this.refreshAccounts();
        }
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    refreshAccounts(): void {
        this.userFilter = this.generalService.getStoredUserFilter();
        this.generalService.getAccountsPlusCurrentCost(this.userFilter).then(accounts => {
            this.accounts = accounts;
            if (this.accounts.length == 0) {
                this.noAccountsPerCurrentUser = true;
            } else {
                this.noAccountsPerCurrentUser = false;
            }
            if (this.accounts.length == 0 && !this.user.isAdvisor) {
                this.generalService.clearStoredAccount();
                this.router.navigate(["account/onboarding"]);
                return;
            }
            if (this.accounts.length == 0 && this.user.isAdvisor) {
                this.generalService.clearStoredAccount();
                return;
            }
            this.withFilter = (this.accounts.length > 5);
            this.accounts.sort((a, b) => {
                if (a.utilityInfo > b.utilityInfo) {
                    return 1;
                }
                if (a.utilityInfo < b.utilityInfo) {
                    return -1;
                }
                return (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0);
            });
            this.filterAccounts();
            if (this.account != null && !this.user.isAdvisor && (this.account.user == null || this.account.user.id != this.user.id || !this.accountIncludes())) {
                this.account = null;
                this.generalService.clearStoredAccount();
            }
            if (this.generalService.getFromSession("defaultAccount") != null) {
                let defaultAccount = this.getAccountFromListByAccountNumber(<string | null>this.generalService.getFromSession("defaultAccount"));
                this.generalService.clearFromSessionByPrefix("defaultAccount");
                if (defaultAccount != null) {
                    this.changeAccount(defaultAccount.id);
                    return;
                }
            }

            if (this.account != null) {
                let accountData = this.getAccountFromListByID(this.account.id);
                if (accountData != null) {
                    this.generalService.switchStoredAccount(this.account!);
                } else {
                    this.account = null;
                    this.generalService.clearStoredAccount();
                }
            }
            if (this.account == null && this.accounts.length > 0) {
                this.changeAccount(this.accounts[0].id);
            }
        });
    }

    getRateClassAbbr(rateClass: string | null): string {
        if (rateClass != null) {
            return this.generalService.getRateClassAbbr(rateClass);
        }
        return "-";
    }

    changeAccount(id: number): void {
        if (this.account == null || this.account.id != id) {
            this.generalService.getAccount(id).then(a => {
                if (a != null) {
                    this.account = a;
                    this.generalService.switchStoredAccount(a);
                }
            });
        }
    }

    switchMode(): void {
        this.refreshAccount = !this.refreshAccount;
    }

    refreshClients(): void {
        this.client = this.generalService.getStoredClient();
        if (this.client != null && this.client.id != 0) {
            this.refreshAccounts();
        }
    }

    changeClient(client: User | null): void {        
        if (client != null) {
            this.noAccountsPerCurrentUser = false;
            this.client = client;
            this.generalService.clearStoredAccount();
        }
    }

    accountIncludes(): boolean {
        if (this.accounts == null || this.account == null) {
            return false;
        }
        for (let i = 0; i < this.accounts.length; i++) {
            if (this.accounts[i].id == this.account.id) {
                return true;
            }
        }
        return false;
    }

    getAccountFromListByID(id: number): AccountSelector | null {
        if (this.accounts == null) {
            return null;
        }
        for (let i = 0; i < this.accounts.length; i++) {
            if (this.accounts[i].id == id) {
                return this.accounts[i];
            }
        }
        return null;
    }

    getAccountFromListByAccountNumber(accountNumber: string | null): AccountSelector | null {
        if (this.accounts == null) {
            return null;
        }
        for (let i = 0; i < this.accounts.length; i++) {
            if (this.accounts[i].accountNumber == accountNumber) {
                return this.accounts[i];
            }
        }
        return null;
    }

    filterAccounts(): void {
        this.filteredAccounts = [];
        if (this.accounts == null) {
            return;
        }
        for (let i = 0; i < this.accounts.length; i++) {
            if (this.keywords == null || this.keywords.length == 0) {
                this.filteredAccounts.push(this.accounts[i]);
                continue;
            }
            if (this.accounts[i].utilityInfo.toLowerCase().includes(this.keywords.toLowerCase())) {
                this.filteredAccounts.push(this.accounts[i]);
                continue;
            }
            if (this.accounts[i].name.toLowerCase().includes(this.keywords.toLowerCase())) {
                this.filteredAccounts.push(this.accounts[i]);
                continue;
            }
        }
    }
    filterClick(event: any): void {
        event.stopPropagation();
    }
}
